import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { api } from "@/lib/api";
import { StrategyVersion, MainSystemPrompt } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const strategyVersionSchema = z.object({
  version_description: z.string().optional(),
  prompt: z.string().min(1, "Prompt is required"),
  main_system_prompt_id: z.string().min(1, "Main system prompt is required"),
  llm_model_provider: z.string().min(1, "LLM model provider is required"),
  llm_model_name: z.string().min(1, "LLM model name is required"),
});

type StrategyVersionFormValues = z.infer<typeof strategyVersionSchema>;

interface StrategyVersionFormProps {
  strategyId: string;
  onVersionCreated: (newVersion: StrategyVersion) => void;
  onCancel: () => void;
}

const StrategyVersionForm: React.FC<StrategyVersionFormProps> = ({
  strategyId,
  onVersionCreated,
  onCancel,
}) => {
  const [mainSystemPrompts, setMainSystemPrompts] = useState<
    MainSystemPrompt[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<StrategyVersionFormValues>({
    resolver: zodResolver(strategyVersionSchema),
    defaultValues: {
      version_description: "",
      prompt: "",
      main_system_prompt_id: "",
      llm_model_provider: "openai",
      llm_model_name: "gpt-4o-2024-08-06",
    },
  });

  useEffect(() => {
    const fetchMainSystemPrompts = async () => {
      try {
        const prompts = await api.getAllMainSystemPrompts();
        setMainSystemPrompts(prompts);
        form.setValue("main_system_prompt_id", prompts[0].id);
      } catch (err) {
        console.error("Failed to fetch main system prompts:", err);
        setError("Failed to fetch main system prompts");
      }
    };

    fetchMainSystemPrompts();
  }, [form]);

  const onSubmit = async (data: StrategyVersionFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const newVersion = await api.createStrategyVersion(strategyId, data);
      onVersionCreated(newVersion);
    } catch (err) {
      console.error("Failed to create strategy version:", err);
      setError("Failed to create strategy version");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mt-4">
      <CardContent className="pt-5">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="version_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Version Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter version description" {...field} />
                  </FormControl>
                  <FormDescription>
                    A brief description of this version
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="prompt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prompt</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter prompt" {...field} rows={5} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="main_system_prompt_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Main System Prompt</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a main system prompt" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {mainSystemPrompts.map((prompt) => (
                        <SelectItem key={prompt.id} value={prompt.id}>
                          {prompt.name}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="llm_model_provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LLM Model Provider</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select LLM Model Provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      {/* Add other providers as needed */}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="llm_model_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LLM Model Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter LLM model name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {error && <div className="text-red-500 mb-4">{error}</div>}

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Version"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default StrategyVersionForm;
